<template>
  <div class="logged">
    <div class="content">
      <div
        :class="kaledoMenuClasses"
      />
      <div
        class="top-menu"
        :class="topMenuClasses"
      >
        <div class="top-menu-container">
          <top-menu
            v-if="showKaledoMenu"
            :company-slug="config.slug"
            :has-marketplace="config.marketplace_client_url"
          />
        </div>
        <layout-navbar />
        <category-menu v-if="isDesktop && showCategoryMenu" />
      </div>
      <layout-sidebar />
      <notifications-sidebar />
      <onboarding-modal
        :is-opened="showOnboarding"
        @close="showOnboarding = false"
      />
      <notice-modal
        v-if="!!notice.id && notice.type !== 'award'"
        :notice="notice"
        :show="showNotice"
        @close="showNotice = false"
      />
      <award-modal
        v-else-if="!!notice.id && notice.type === 'award'"
        :show="showAward"
        :notice="notice"
        :company-slug="config.slug"
        @close="showAward = false"
      />
      <router-view />
    </div>
    <layout-footer
      :display-scroll-top="position < -200"
      :config="config"
      :hide-developed="hideDeveloped"
    />
  </div>
</template>

<script>
import TopMenu from '@/components/Layout/TopMenu.vue'
import LayoutNavbar from '@containers/Layout/LayoutNavbar'
import LayoutSidebar from '@containers/Layout/LayoutSidebar'
import NotificationsSidebar from '@containers/Layout/NotificationsSidebar'
import CategoryMenu from '@containers/Category/CategoryMenu'
import LayoutFooter from '@components/Layout/LayoutFooter'
import { mapGetters, mapActions } from 'vuex'
import { NOTICE, USER, LAYOUT_IS_OPENED, LAYOUT_CLOSE, CONFIG_MAIN, COMPANY, CONFIG_SIGNIN } from '@store/types'
import AwardModal from '@/containers/Notices/AwardModal.vue'
import OnboardingModal from '@/containers/Login/OnboardingModalContainer.vue'

export default {
  components: {
    TopMenu,
    LayoutNavbar,
    LayoutSidebar,
    NotificationsSidebar,
    CategoryMenu,
    LayoutFooter,
    AwardModal,
    OnboardingModal,
    NoticeModal: () => import('@containers/Notices/NoticeModal2')
  },

  props: {
    warnExplorer: Boolean
  },

  data () {
    return {
      position: 0,
      lastPosition: 0,
      showAward: false,
      showNotice: false,
      showOnboarding: false
    }
  },

  computed: {
    ...mapGetters({
      notice: NOTICE,
      modalOpened: LAYOUT_IS_OPENED,
      config: CONFIG_MAIN,
      company: COMPANY,
      config_sig: CONFIG_SIGNIN
    }),

    toTop () {
      const toTop = this.lastPosition <= this.position

      return toTop
    },

    showKaledoMenu () {
      if (this.company && this.company.permissions && this.company.permissions.indexOf('marketplace') !== -1) return true
      return false
    },

    showNavbar () {
      if (this.position > -60) return true

      const showNavbar = this.toTop

      return showNavbar
    },

    hideDeveloped () {
      return (this.config_sig || {}).developedByConvenia === false
    },

    showRewardsAndDiscountsTabs () {
      return !this.isDesktop && this.$can('engagement')
    },

    showCategoryMenu () {
      const routesNotAllowed = ['Recompensas', 'Catálogo de Recompensas', 'Resgate de Recompensas']
      return !routesNotAllowed.includes(this.$route.name)
    },

    kaledoMenuClasses () {
      let classes = ''
      if (this.showKaledoMenu) classes += 'kaledo-menu'
      if (this.showCategoryMenu) classes += ' top-menu-spaceholder'
      if (!this.showCategoryMenu) classes += ' very-reduced-top-menu-spaceholder'
      else classes += ' reduced-top-menu-spaceholder'
      if (this.showRewardsAndDiscountsTabs) classes += ' rewards-discounts-tabs'
      return classes
    },

    topMenuClasses () {
      let classes = ''
      if (this.showNavbar) classes += '-shown'
      if (this.warnExplorer && this.isDesktop) classes += ' -warn-explorer'
      if (this.showKaledoMenu) classes += ' -show-k-menu'
      return classes
    }
  },

  watch: {
    modalOpened (v) {
      const styles = {
        overflow: v ? 'hidden' : '',
        position: v ? 'relative' : '',
        height: v ? '100%' : ''
      }

      Object.assign(document.body.style, styles)
      Object.assign(document.documentElement.style, styles)
    }
  },

  async mounted () {
    this.handleScroll()
    if (this.company.permissions === null) {
      this.getCompany()
    }

    document.addEventListener('scroll', this.handleScroll)

    await this.getNotice()
    this.checkNotice()
  },

  beforeDestroy () {
    document.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    ...mapActions({
      closeModals: LAYOUT_CLOSE,
      getNotice: NOTICE,
      user: USER,
      getCompany: COMPANY
    }),

    handleScroll () {
      const boundingRect = document.body.getBoundingClientRect().top
      this.lastPosition = this.position
      this.position = boundingRect
    },

    checkNotice () {
      let src = require('@/assets/img/awards/award-modal-bg.jpeg')
      if (this.notice.type !== 'award') {
        src = this.isDesktop ? this.notice.image : this.notice.image_mobile
      }
      const img = new Image()
      img.src = `${src}?${(new Date().getTime())}`
      img.onload = () => {
        this.showNotice = !!(this.notice || {}).id && this.notice.type !== 'award'
        this.showAward = !!(this.notice || {}).id && this.notice.type === 'award'
      }
      this.showOnboarding = this.notice.firstAccess
    }
  }
}
</script>

<style lang="scss">
@import '~@style/reference';

.logged {
  background-color: $background-color;
  min-height: 100vh;

  &>.content {
    min-height: calc(100vh - 180px);
  }
}

.top-menu {
  position: fixed;
  top: -70px;
  z-index: 2;
  left: 0;
  width: 100%;
  background-color: $white-color;
  will-change: top;
  transition: top .3s ease;

  &.-show-k-menu {
    top: -109px
  }

  &.-warn-explorer {
    top: 10px;
  }

  &.-shown {
    top: 0;

    &.-warn-explorer {
      top: 80px;
    }
  }
}

.reduced-top-menu-spaceholder {
  height: 120px;
}
.very-reduced-top-menu-spaceholder {
  height: 70px;
}

.top-menu-spaceholder {
  height: 120px;

  @include mobile {
    height: 70px;
  }

  &.kaledo-menu {
    height: 179px;

    @include desktop {
      height: 153px;
    }
  }
}

.rewards-discounts-tabs {
  height: 110px;
}

.top-menu-container {
  width: 100%;
  box-shadow: 0 -1px 5px 0 rgba(0, 0, 0, 0.1);
}
</style>



// WEBPACK FOOTER //
// src/views/Logged.vue